import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { HomeBuildFocusId } from '../lib/generation-intent';
import {
  appendArtifact,
  createEmptySession,
  loadPersistedSession,
  persistSession,
} from '../lib/canvas-session-store';
import { hydrateSessionFromSite } from '../lib/hydrate-site-session';
import type { CanvasSessionRecord, EditWorkingBuffer } from '../lib/canvas-session-types';
import { draftToGenerationRequest } from '../lib/draft-to-generation';
import { mergeSiteOnboardingDraft, seedDraftFromPrompt } from '../lib/onboarding-draft';
import type { OnboardingChatMessage, SiteOnboardingDraft } from '../lib/onboarding-types';
import { appendVoiceTranscriptCoalesced } from '../lib/voice-transcript-coalesce';
import {
  isLandingEditorApiConfigured,
  postOnboardingChatTurn,
  postStartGeneration,
  subscribeGenerationStream,
} from '../lib/landing-editor-api';
import { postSiteEdit, postSitePublish } from '../lib/editing-agent-api';
import type { SiteListItem } from '../lib/sites-api';

export interface StartFromIntentInput {
  query: string;
  focus: HomeBuildFocusId;
}

export interface RequestSiteEditInput {
  instruction: string;
  page?: string;
}

interface CanvasSessionContextValue {
  session: CanvasSessionRecord;
  startFromIntent: (input: StartFromIntentInput) => Promise<void>;
  /**
   * Continue the onboarding brief conversation. Appends the user's message,
   * runs another onboarding chat turn, and merges the returned draft patch.
   * Does NOT trigger generation — generation is gated behind `startGeneration`.
   */
  continueOnboarding: (message: string) => Promise<void>;
  /**
   * Send a chat message in any active phase. During onboarding, delegates to
   * `continueOnboarding`. After site creation, keeps the session phase and
   * appends to the conversation for follow-up edits/questions.
   */
  sendChatMessage: (message: string) => Promise<void>;
  /** Mirror Gemini Live transcript fragments into the onboarding chat thread. */
  appendVoiceTranscript: (detail: {
    role: 'user' | 'assistant';
    text: string;
    timestamp: string;
  }) => void;
  /** Two-way edit of the assembled brief (config panel <-> conversation state). */
  updateDraft: (patch: Partial<SiteOnboardingDraft>) => void;
  /**
   * Explicit "Build" action — the ONLY path that fires generate-stream.
   * Maps the current brief to a generation request, starts generation, and
   * subscribes to the event stream. No-op if a build is already in flight.
   */
  startGeneration: () => Promise<void>;
  requestSiteEdit: (input: RequestSiteEditInput) => Promise<void>;
  publishSite: () => Promise<void>;
  resetSession: () => void;
  /** Switch the active session to a site. Hydrates from site list metadata when not persisted locally. */
  loadSite: (siteId: string, siteMeta?: SiteListItem) => boolean;
}

const CanvasSessionContext = createContext<CanvasSessionContextValue | null>(null);

export function CanvasSessionProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [session, setSession] = useState<CanvasSessionRecord>(() => {
    return loadPersistedSession(null) ?? createEmptySession();
  });
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const inFlightRef = useRef(false);
  const mutationInFlightRef = useRef(false);

  useEffect(() => {
    persistSession(session);
  }, [session]);

  useEffect(() => {
    return () => {
      unsubscribeRef.current?.();
    };
  }, []);

  const resetSession = useCallback(() => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    setSession(createEmptySession());
  }, []);

  const loadSite = useCallback((siteId: string, siteMeta?: SiteListItem): boolean => {
    let persisted = loadPersistedSession(siteId);
    if (!persisted && siteMeta) {
      persisted = hydrateSessionFromSite(siteMeta);
      persistSession(persisted);
    }
    if (!persisted) {
      return false;
    }
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    inFlightRef.current = false;
    setSession({ ...persisted, updatedAt: Date.now() });
    return true;
  }, []);

  const startFromIntent = useCallback(async (input: StartFromIntentInput) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    unsubscribeRef.current?.();
    unsubscribeRef.current = null;

    const userMessage: OnboardingChatMessage = { role: 'user', content: input.query.trim() };
    let next = createEmptySession();
    next = {
      ...next,
      phase: 'onboarding',
      draft: seedDraftFromPrompt(input.query, input.focus === 'landing-pages' ? 'single' : 'single'),
      messages: [userMessage],
      lastError: null,
    };
    next = appendArtifact(next, {
      kind: 'user-message',
      label: 'Home prompt',
      payload: { query: input.query, focus: input.focus },
    });
    setSession(next);

    if (!isLandingEditorApiConfigured()) {
      setSession((prev) => ({
        ...prev,
        phase: 'error',
        lastError: 'Configure VITE_LANDI_AGENTS_URL for generation APIs',
      }));
      inFlightRef.current = false;
      return;
    }

    try {
      const chat = await postOnboardingChatTurn({
        messages: next.messages,
        draft: next.draft,
      });

      const mergedDraft = mergeSiteOnboardingDraft(next.draft, chat.draft);
      const assistantMessage: OnboardingChatMessage = {
        role: 'assistant',
        content: chat.assistantMessage,
      };

      // First submit stops at the conversational brief — it does NOT start
      // generation. The user drives generation later via the explicit Build
      // action (`startGeneration`), so the phase stays `onboarding` here.
      let withChat: CanvasSessionRecord = {
        ...next,
        draft: mergedDraft,
        messages: [...next.messages, assistantMessage],
        phase: 'onboarding',
      };
      withChat = appendArtifact(withChat, {
        kind: 'assistant-message',
        label: 'Onboarding assistant',
        payload: { message: chat.assistantMessage },
      });
      setSession(withChat);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Session start failed';
      setSession((prev) => ({
        ...prev,
        phase: 'error',
        lastError: message,
      }));
    } finally {
      // Onboarding turn complete; release the guard so the user can continue
      // the brief conversation. Generation acquires its own guard.
      inFlightRef.current = false;
    }
  }, []);

  const continueOnboarding = useCallback(async (message: string) => {
    const trimmed = message.trim();
    if (!trimmed || inFlightRef.current) return;
    inFlightRef.current = true;

    const userMessage: OnboardingChatMessage = { role: 'user', content: trimmed };
    let base: CanvasSessionRecord | null = null;
    setSession((prev) => {
      base = appendArtifact(
        {
          ...prev,
          phase: 'onboarding',
          messages: [...prev.messages, userMessage],
          lastError: null,
        },
        { kind: 'user-message', label: 'Brief reply', payload: { message: trimmed } },
      );
      return base;
    });

    if (!base) {
      inFlightRef.current = false;
      return;
    }
    const current = base as CanvasSessionRecord;

    if (!isLandingEditorApiConfigured()) {
      setSession((prev) => ({
        ...prev,
        phase: 'error',
        lastError: 'Configure VITE_LANDI_AGENTS_URL for generation APIs',
      }));
      inFlightRef.current = false;
      return;
    }

    try {
      const chat = await postOnboardingChatTurn({
        messages: current.messages,
        draft: current.draft,
      });
      const mergedDraft = mergeSiteOnboardingDraft(current.draft, chat.draft);
      const assistantMessage: OnboardingChatMessage = {
        role: 'assistant',
        content: chat.assistantMessage,
      };
      setSession((prev) =>
        appendArtifact(
          {
            ...prev,
            draft: mergedDraft,
            messages: [...prev.messages, assistantMessage],
            phase: 'onboarding',
            lastError: null,
          },
          { kind: 'assistant-message', label: 'Onboarding assistant', payload: { message: chat.assistantMessage } },
        ),
      );
    } catch (err) {
      const errMessage = err instanceof Error ? err.message : 'Onboarding turn failed';
      setSession((prev) => ({ ...prev, phase: 'error', lastError: errMessage }));
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  const sendChatMessage = useCallback(
    async (message: string) => {
      const trimmed = message.trim();
      if (!trimmed || inFlightRef.current) return;

      const postComplete =
        session.phase === 'complete' ||
        session.phase === 'editing' ||
        session.phase === 'publishing' ||
        (session.phase === 'error' && Boolean(session.siteId));

      if (!postComplete) {
        await continueOnboarding(trimmed);
        return;
      }

      inFlightRef.current = true;
      const userMessage: OnboardingChatMessage = { role: 'user', content: trimmed };
      let base: CanvasSessionRecord | null = null;
      setSession((prev) => {
        base = appendArtifact(
          {
            ...prev,
            messages: [...prev.messages, userMessage],
            lastError: null,
          },
          { kind: 'user-message', label: 'Follow-up', payload: { message: trimmed } },
        );
        return base;
      });

      if (!base) {
        inFlightRef.current = false;
        return;
      }
      const current = base as CanvasSessionRecord;

      if (!isLandingEditorApiConfigured()) {
        setSession((prev) => ({
          ...prev,
          lastError: 'Configure VITE_LANDI_AGENTS_URL for chat APIs',
        }));
        inFlightRef.current = false;
        return;
      }

      try {
        const chat = await postOnboardingChatTurn({
          messages: current.messages,
          draft: current.draft,
        });
        const mergedDraft = mergeSiteOnboardingDraft(current.draft, chat.draft);
        const assistantMessage: OnboardingChatMessage = {
          role: 'assistant',
          content: chat.assistantMessage,
        };
        setSession((prev) =>
          appendArtifact(
            {
              ...prev,
              draft: mergedDraft,
              messages: [...prev.messages, assistantMessage],
              lastError: null,
            },
            {
              kind: 'assistant-message',
              label: 'Follow-up assistant',
              payload: { message: chat.assistantMessage },
            },
          ),
        );
      } catch (err) {
        const errMessage = err instanceof Error ? err.message : 'Chat turn failed';
        setSession((prev) => ({ ...prev, lastError: errMessage }));
      } finally {
        inFlightRef.current = false;
      }
    },
    [continueOnboarding, session.phase, session.siteId],
  );

  const updateDraft = useCallback((patch: Partial<SiteOnboardingDraft>) => {
    setSession((prev) => ({
      ...prev,
      draft: { ...prev.draft, ...patch },
      updatedAt: Date.now(),
    }));
  }, []);

  // Subscribe to the generation SSE stream and wire session updates. Extracted
  // so both an explicit Build (`startGeneration`) and reload rehydration can
  // reuse the exact same handlers. Returns the unsubscribe fn.
  const attachGenerationStream = useCallback((generationSessionId: string): (() => void) => {
    return subscribeGenerationStream(generationSessionId, {
      onEvent: (payload) => {
        setSession((prev) =>
          appendArtifact(prev, { kind: 'generation-event', label: 'Generation event', payload }),
        );
      },
      onComplete: ({ siteId }) => {
        setSession((prev) => ({ ...prev, siteId, phase: 'complete', lastError: null }));
        inFlightRef.current = false;
      },
      onError: ({ message }) => {
        setSession((prev) => ({ ...prev, phase: 'error', lastError: message }));
        inFlightRef.current = false;
      },
    });
  }, []);

  // Rehydrate a mid-generation session after reload. Without this, a persisted
  // `phase: 'generating'` leaves the UI stuck on "Building…" with Build disabled
  // and no live stream (deadlock). Re-subscribe when we have a session id; if the
  // stream is gone the SSE `onError` downgrades to `error` so Build can retry.
  const rehydratedRef = useRef(false);
  useEffect(() => {
    if (rehydratedRef.current) return;
    rehydratedRef.current = true;
    if (session.phase !== 'generating') return;
    if (session.generationSessionId && isLandingEditorApiConfigured()) {
      inFlightRef.current = true;
      unsubscribeRef.current?.();
      unsubscribeRef.current = attachGenerationStream(session.generationSessionId);
    } else {
      // Nothing to resume — drop back to the brief so the user can Build again.
      setSession((prev) => ({
        ...prev,
        phase: 'onboarding',
        lastError: 'Previous build was interrupted — review the brief and build again.',
      }));
    }
    // Run once on mount against the initially-persisted session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const appendVoiceTranscript = useCallback(
    (detail: { role: 'user' | 'assistant'; text: string; timestamp: string }) => {
      if (!detail.text.trim()) {
        return;
      }
      setSession((prev) => ({
        ...prev,
        messages: appendVoiceTranscriptCoalesced(prev.messages, detail),
      }));
    },
    [],
  );

  const startGeneration = useCallback(async () => {
    if (inFlightRef.current) return;

    let snapshot: CanvasSessionRecord | null = null;
    setSession((prev) => {
      snapshot = prev;
      return prev;
    });
    const current = snapshot as CanvasSessionRecord | null;
    if (!current) return;
    // Only build from a brief that has been started; don't allow re-entrancy
    // while already generating.
    if (current.phase === 'generating') return;

    if (!isLandingEditorApiConfigured()) {
      setSession((prev) => ({
        ...prev,
        phase: 'error',
        lastError: 'Configure VITE_LANDI_AGENTS_URL for generation APIs',
      }));
      return;
    }

    inFlightRef.current = true;
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;

    setSession((prev) => ({ ...prev, phase: 'generating', lastError: null }));

    const promptOverride =
      current.messages.find((m) => m.role === 'user')?.content ?? current.draft.description;
    const generationBody = draftToGenerationRequest(current.draft, {
      siteId: current.siteId ?? undefined,
      promptOverride: promptOverride?.trim() || undefined,
    });

    try {
      const started = await postStartGeneration(generationBody);
      setSession((prev) => ({ ...prev, generationSessionId: started.sessionId, phase: 'generating' }));

      unsubscribeRef.current = attachGenerationStream(started.sessionId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Generation failed to start';
      setSession((prev) => ({ ...prev, phase: 'error', lastError: message }));
      inFlightRef.current = false;
    }
  }, [attachGenerationStream]);

  const requestSiteEdit = useCallback(async (input: RequestSiteEditInput) => {
    if (mutationInFlightRef.current) {
      return;
    }
    const instruction = input.instruction.trim();
    if (!instruction) {
      return;
    }

    mutationInFlightRef.current = true;

    let siteId: string | null = null;
    setSession((prev) => {
      siteId = prev.siteId;
      if (!siteId) {
        return { ...prev, lastError: 'No site to edit' };
      }
      const page = input.page ?? 'index.html';
      const buffer: EditWorkingBuffer = { instruction, page, updatedAt: Date.now() };
      return appendArtifact(
        { ...prev, phase: 'editing', editWorkingBuffer: buffer, lastError: null },
        { kind: 'edit-request', label: 'Edit instruction', payload: { instruction, page } },
      );
    });

    if (!siteId) {
      mutationInFlightRef.current = false;
      return;
    }

    const page = input.page ?? 'index.html';

    try {
      const result = await postSiteEdit({ siteId, instruction, page });
      setSession((prev) =>
        appendArtifact(
          {
            ...prev,
            phase: 'complete',
            editWorkingBuffer: null,
            lastError: null,
          },
          { kind: 'edit-result', label: 'Edit applied', payload: result },
        ),
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Edit failed';
      setSession((prev) => ({
        ...prev,
        phase: 'error',
        lastError: message,
      }));
    } finally {
      mutationInFlightRef.current = false;
    }
  }, []);

  const publishSite = useCallback(async () => {
    if (mutationInFlightRef.current) {
      return;
    }
    mutationInFlightRef.current = true;

    let siteId: string | null = null;
    setSession((prev) => {
      siteId = prev.siteId;
      if (!siteId) {
        return { ...prev, lastError: 'No site to publish' };
      }
      return { ...prev, phase: 'publishing', lastError: null };
    });

    if (!siteId) {
      mutationInFlightRef.current = false;
      return;
    }

    try {
      const result = await postSitePublish({ siteId });
      setSession((prev) =>
        appendArtifact(
          {
            ...prev,
            phase: 'complete',
            publishedUrl: result.url ?? prev.publishedUrl,
            lastError: null,
          },
          { kind: 'publish-result', label: 'Published', payload: result },
        ),
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Publish failed';
      setSession((prev) => ({
        ...prev,
        phase: 'error',
        lastError: message,
      }));
    } finally {
      mutationInFlightRef.current = false;
    }
  }, []);

  const value = useMemo<CanvasSessionContextValue>(
    () => ({
      session,
      startFromIntent,
      continueOnboarding,
      sendChatMessage,
      appendVoiceTranscript,
      updateDraft,
      startGeneration,
      requestSiteEdit,
      publishSite,
      resetSession,
      loadSite,
    }),
    [
      session,
      startFromIntent,
      continueOnboarding,
      sendChatMessage,
      appendVoiceTranscript,
      updateDraft,
      startGeneration,
      requestSiteEdit,
      publishSite,
      resetSession,
      loadSite,
    ],
  );

  return (
    <CanvasSessionContext.Provider value={value}>{children}</CanvasSessionContext.Provider>
  );
}

export function useCanvasSession(): CanvasSessionContextValue {
  const ctx = useContext(CanvasSessionContext);
  if (!ctx) {
    throw new Error('useCanvasSession must be used within CanvasSessionProvider');
  }
  return ctx;
}
